/**
 * Pure Regeln des Moduls "Planungsphase".
 *
 * Hier steht alles, was ohne DOM, ohne Supabase und ohne jsdom entscheidbar
 * ist — das Projekt uebersetzt Tests in der Node-Umgebung (kein jsdom), also
 * ist eine Regel nur dann geprueft, wenn sie hier liegt und nicht in einer
 * Komponente. Stehende Entscheidung im Repo: duenner Container, Logik in
 * Hooks und puren Funktionen.
 */

import { ANHANG_2_ARBEITEN, FRAGEN_KATALOG, GEBAEUDETYP_WERTE, KATALOG_VERSION } from './fragenkatalog';
import type {
    Anhang2Treffer,
    DritterNachPara4,
    FrageAdressat,
    FrageKritikalitaet,
    FrageStatus,
    FreigabePruefung,
    Gebaeudetyp,
    KatalogAuswahl,
    KatalogFrage,
    KatalogKapitel,
    NeuePlanungsfrage,
    PflichtArt,
    PflichtEintrag,
    PflichtStand,
    Planungsfrage,
    Planungshinweis,
    Planungsphase,
    PlanungKennzahlen,
    ProjektMerkmale,
    Restrisiko,
    SchwellenwertErgebnis,
} from './types';

// ═══════════════════════════════════════════════════════════════════════════
// TRIGGER — welche Frage trifft auf dieses Projekt zu?
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ein Merkmal ist dreiwertig. `undefined` heisst "noch nicht beantwortet"
 * und MUSS wie "kann sein" wirken: sonst verschwaende die Frage nach Asbest,
 * solange das Baujahr noch fehlt — also genau dann, wenn sie am wichtigsten
 * ist. Nur ein ausdrueckliches `false` blendet aus.
 */
const merkmalMoeglich = (wert: boolean | undefined): boolean => wert !== false;

/**
 * Trifft die Katalogfrage nach den Projektmerkmalen zu?
 *
 * Ohne `trigger` gilt die Frage immer. Mit `trigger` gilt sie, wenn
 * mindestens eines der `wenn`-Merkmale moeglich ist (ODER-Verknuepfung) und
 * keines der `nichtWenn`-Merkmale zutrifft. Die Schwellen wirken zusaetzlich,
 * lassen die Frage aber bei unbekanntem Wert stehen.
 */
export const frageTrifftZu = (frage: KatalogFrage, merkmale: ProjektMerkmale): boolean => {
    const t = frage.trigger;
    if (!t) return true;
    const m: any = merkmale || {};

    if (t.nichtWenn && t.nichtWenn.length) {
        // Ausschluss wirkt nur bei ausdruecklichem `true`.
        if (t.nichtWenn.some(k => m[k] === true)) return false;
    }

    if (t.schwelle) {
        const wert = m[t.schwelle.merkmal];
        if (typeof wert === 'number' && wert < t.schwelle.ab) return false;
    }

    if (t.schwelleBis) {
        const wert = m[t.schwelleBis.merkmal];
        if (typeof wert === 'number' && wert > t.schwelleBis.bis) return false;
    }

    // Gebaeudetyp: nur ein ausdruecklich gewaehlter Typ blendet aus.
    if (t.nichtBeiGebaeudetyp && t.nichtBeiGebaeudetyp.length) {
        const typ = gebaeudetypAus(m.gebaeudetyp);
        if (typ && t.nichtBeiGebaeudetyp.includes(typ)) return false;
    }

    // Pflicht: nur ein SICHERES „keine" blendet aus — dieselbe Rechnung wie
    // Maske, Schnappschuss und Ampel (`schwellenwerteAusMerkmalen`).
    if (t.nurBeiPflicht) {
        const stand = pflichtStandAus(schwellenwerteAusMerkmalen(m, ''), t.nurBeiPflicht);
        if (stand === 'keine') return false;
    }

    if (t.wenn && t.wenn.length) {
        return t.wenn.some(k => merkmalMoeglich(m[k]));
    }

    return true;
};

/**
 * Ist die Frage in der aktuellen Leistungsphase noch beeinflussbar?
 *
 * Eine nicht mehr beeinflussbare Frage ist NICHT gegenstandslos — sie ist der
 * Anlass fuer eine Bedenkenanzeige. Dieselbe Frage, andere Eskalationsstufe.
 * Ohne bekannte Leistungsphase gilt alles als beeinflussbar.
 */
export const istBeeinflussbar = (frage: KatalogFrage, lph?: number | null): boolean => {
    if (typeof lph !== 'number' || !isFinite(lph)) return true;
    if (typeof frage.lphVon === 'number' && lph < frage.lphVon) return true;
    if (typeof frage.lphBis === 'number' && lph > frage.lphBis) return false;
    return true;
};

/**
 * Ist die Frage in der aktuellen Leistungsphase schon faellig?
 *
 * Fragen zu Ausschreibung, Vergabe und Uebergabe an die Ausfuehrung
 * (`lphVon`) haben in LPH 2 noch keinen Adressaten, der sie beantworten
 * koennte — sie entstehen erst ab ihrer Leistungsphase und werden dann ueber
 * „nachziehen" ergaenzt. Ohne bekannte Leistungsphase ist alles faellig:
 * lieber eine Frage zu frueh als eine vergessene.
 */
export const frageFaellig = (frage: KatalogFrage, lph?: number | null): boolean => {
    if (typeof lph !== 'number' || !isFinite(lph)) return true;
    return !(typeof frage.lphVon === 'number' && lph < frage.lphVon);
};

/** Wird die Katalogfrage fuer dieses Projekt angelegt? Merkmale UND Leistungsphase. */
const frageAnlegen = (frage: KatalogFrage, merkmale: ProjektMerkmale, lph?: number | null): boolean =>
    frageTrifftZu(frage, merkmale) && frageFaellig(frage, lph);

/** Der auf das Projekt zugeschnittene Katalog, samt Begruendung je Zeile. */
export const waehleKatalogFragen = (
    merkmale: ProjektMerkmale,
    lph?: number | null,
    katalog: KatalogFrage[] = FRAGEN_KATALOG,
): KatalogAuswahl[] =>
    katalog.map(frage => ({
        frage,
        trifftZu: frageTrifftZu(frage, merkmale),
        beeinflussbar: istBeeinflussbar(frage, lph),
    }));

// ═══════════════════════════════════════════════════════════════════════════
// ERZEUGEN UND NACHZIEHEN
// ═══════════════════════════════════════════════════════════════════════════

/** Baut aus einer Katalogfrage die Projektinstanz. */
export const katalogfrageZuFrage = (frage: KatalogFrage, sortierung: number): NeuePlanungsfrage => ({
    code: frage.code,
    kapitel: frage.kapitel,
    frage: frage.frage,
    adressat: [...frage.adressat],
    empfaenger_name: null,
    empfaenger_email: null,
    typ: frage.typ,
    kritikalitaet: frage.kritikalitaet,
    status: 'offen',
    antwort: '',
    bewertung: '',
    rechtsgrundlage: frage.rechtsgrundlage ? [...frage.rechtsgrundlage] : [],
    zielobjekt: frage.zielobjekt || 'keins',
    gestellt_am: null,
    frist_am: null,
    beantwortet_am: null,
    uebernommen_am: null,
    sortierung,
    eigene_frage: false,
});

/** Erstbefuellung: alle zutreffenden und schon faelligen Fragen des Katalogs. */
export const erzeugeFragen = (
    merkmale: ProjektMerkmale,
    katalog: KatalogFrage[] = FRAGEN_KATALOG,
    lph: number | null = null,
): NeuePlanungsfrage[] =>
    katalog
        .filter(fr => frageAnlegen(fr, merkmale, lph))
        .map((fr, i) => katalogfrageZuFrage(fr, (i + 1) * 10));

/**
 * Der Katalog soll "immer weiter gefuellt" werden koennen: aendern sich die
 * Merkmale (Asbest doch vorhanden, Kran kommt hinzu), kommen Fragen dazu.
 *
 * BESTEHENDE FRAGEN WERDEN NIE ENTFERNT. Eine Frage, die einmal gestellt
 * wurde, verschwindet nicht, nur weil ein Haken zurueckgenommen wurde — sonst
 * verschwaende mit ihr der Nachweis, dass gefragt wurde. Wer sie loswerden
 * will, setzt sie auf `gegenstandslos`; das ist eine dokumentierte Handlung.
 */
export const neueFragenNachziehen = (
    vorhanden: Planungsfrage[],
    merkmale: ProjektMerkmale,
    katalog: KatalogFrage[] = FRAGEN_KATALOG,
    lph: number | null = null,
): NeuePlanungsfrage[] => {
    const bekannt = new Set((vorhanden || []).map(f => f.code));
    const maxSort = (vorhanden || []).reduce((a, f) => Math.max(a, f.sortierung || 0), 0);
    return katalog
        .filter(fr => !bekannt.has(fr.code) && frageAnlegen(fr, merkmale, lph))
        .map((fr, i) => katalogfrageZuFrage(fr, maxSort + (i + 1) * 10));
};

/** Status, bei denen eine Frage noch nie das Haus verlassen hat und keine
 *  Antwort traegt — nur sie kommen fuer den Sammel-Knopf in Frage. */
const UNBERUEHRT: readonly FrageStatus[] = ['offen'];

/** Laengste vorhandene Bewertung, die der Sammel-Knopf noch anfasst (Zeichen). */
export const GEGENSTANDSLOS_MAX_BEWERTUNG = 1000;

/**
 * Fragen eines bestehenden Katalogs, die nach den AKTUELLEN Merkmalen nicht
 * mehr ausgeloest wuerden — der Vorschlag fuer den Sammel-Knopf
 * „gegenstandslos".
 *
 * Bewusst eng, damit der Knopf nie Beweise beseitigt:
 *  - nur Katalogfragen, keine eigenen (die hat ein Mensch gezielt gestellt);
 *  - nur `offen` ohne Antwort, ohne Versand und ohne Stelldatum — eine
 *    gestellte oder beantwortete Frage ist Teil der Akte;
 *  - nur der Merkmal-Ausloeser zaehlt, nicht die Leistungsphase: eine noch
 *    nicht faellige Frage ist nicht gegenstandslos, nur zu frueh.
 * Gestrichen wird nichts — der Aufrufer setzt den Status und schreibt die
 * Begruendung (`gegenstandslosBegruendung`).
 */
export const nichtMehrZutreffend = (
    fragen: readonly Planungsfrage[],
    merkmale: ProjektMerkmale,
    katalog: KatalogFrage[] = FRAGEN_KATALOG,
): Planungsfrage[] => {
    const nachCode = new Map(katalog.map(k => [k.code, k]));
    return (fragen || []).filter(f => {
        if (!f || f.eigene_frage || !UNBERUEHRT.includes(f.status)) return false;
        // Ohne trim: der Schutz im UPDATE laesst nur NULL oder '' durch — eine
        // Antwort aus Leerzeichen wuerde sonst bei jedem Klick vorgeschlagen und
        // nie gesetzt.
        if (f.gestellt_am || f.antwort) return false;
        // Der alte Bewertungstext steht im UPDATE als Filter in der URL — ein
        // sehr langer bleibt bei der Einzelbearbeitung.
        if (String(f.bewertung || '').length > GEGENSTANDSLOS_MAX_BEWERTUNG) return false;
        const k = nachCode.get(f.code);
        return !!k && !frageTrifftZu(k, merkmale);
    });
};

/**
 * Begruendung, die der Sammel-Knopf in die interne Bewertung schreibt —
 * damit spaeter nachvollziehbar ist, WARUM die Frage gegenstandslos wurde.
 * Eine vorhandene Bewertung bleibt stehen, die Begruendung kommt darunter.
 */
export const gegenstandslosBegruendung = (bisher: string | null | undefined, tagIso: string): string => {
    const tag = tagIso.slice(0, 10).split('-').reverse().join('.');
    const satz = `Gegenstandslos gesetzt am ${tag}: nach den Projektmerkmalen nicht mehr zutreffend (Sammelaktion, Frage war nicht versendet).`;
    const alt = String(bisher || '').trimEnd();
    return alt ? `${alt}\n${satz}` : satz;
};

/** Freie Zusatzfrage. Der Code traegt bewusst ein eigenes Praefix, damit er
 *  nie mit einem Katalogcode kollidiert. */
export const eigeneFrage = (
    kapitel: KatalogKapitel,
    text: string,
    adressat: FrageAdressat[],
    kritikalitaet: FrageKritikalitaet,
    vorhanden: Planungsfrage[],
): NeuePlanungsfrage => {
    const lfd = (vorhanden || []).filter(f => f.eigene_frage).length + 1;
    const maxSort = (vorhanden || []).reduce((a, f) => Math.max(a, f.sortierung || 0), 0);
    return {
        code: `PL-X-${String(lfd).padStart(2, '0')}`,
        kapitel,
        frage: (text || '').trim(),
        adressat: adressat && adressat.length ? [...adressat] : ['BH'],
        empfaenger_name: null,
        empfaenger_email: null,
        typ: 'T',
        kritikalitaet,
        status: 'offen',
        antwort: '',
        bewertung: '',
        rechtsgrundlage: [],
        zielobjekt: 'keins',
        gestellt_am: null,
        frist_am: null,
        beantwortet_am: null,
        uebernommen_am: null,
        sortierung: maxSort + 10,
        eigene_frage: true,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// SCHWELLENWERTE — §§ 2 und 3 BaustellV
// ═══════════════════════════════════════════════════════════════════════════

export interface SchwellenwertEingang {
    arbeitstage?: number;
    maxBeschaeftigte?: number;
    personentage?: number;
    mehrereArbeitgeber?: boolean;
    merkmale?: ProjektMerkmale;
    /** Ausdrueckliche Antworten zu Anhang II, sofern der Nutzer sie gesetzt
     *  hat. Ueberschreibt die aus den Merkmalen abgeleitete Vermutung. */
    anhang2Antworten?: Record<number, boolean | undefined>;
    /** Wird in das Ergebnis geschrieben. Kein `new Date()` in der puren
     *  Funktion — der Aufrufer gibt den Zeitpunkt vor, sonst ist die
     *  Funktion nicht testbar. */
    jetztIso: string;
}

const zahl = (v: any): number | undefined =>
    typeof v === 'number' && isFinite(v) && v >= 0 ? v : undefined;

/** Anhang-II-Treffer aus Merkmalen und ausdruecklichen Antworten. */
export const pruefeAnhang2 = (
    merkmale: ProjektMerkmale,
    antworten?: Record<number, boolean | undefined>,
): Anhang2Treffer[] => {
    const m: any = merkmale || {};
    return ANHANG_2_ARBEITEN.map(a => {
        const ausdruecklich = antworten ? antworten[a.nummer] : undefined;
        let zutreffend: boolean | undefined = ausdruecklich;
        let begruendung = '';

        if (ausdruecklich === undefined) {
            if (a.nummer === 1) {
                // Nr. 1 haengt an zwei Zahlen, nicht an einem Haken.
                const tiefe = zahl(m.grabentiefeM);
                const hoehe = zahl(m.absturzhoeheM);
                if (typeof tiefe === 'number' && tiefe > 5) {
                    zutreffend = true;
                    begruendung = `Baugruben-/Grabentiefe ${tiefe} m (über 5 m).`;
                } else if (typeof hoehe === 'number' && hoehe > 7) {
                    zutreffend = true;
                    begruendung = `Absturzhöhe ${hoehe} m (über 7 m).`;
                } else if (typeof tiefe === 'number' && typeof hoehe === 'number') {
                    zutreffend = false;
                    begruendung = `Tiefe ${tiefe} m und Absturzhöhe ${hoehe} m liegen unter den Schwellen.`;
                } else {
                    begruendung = 'Tiefe bzw. Absturzhöhe noch nicht angegeben.';
                }
            } else if (a.merkmal) {
                const wert = m[a.merkmal];
                if (wert === true) {
                    zutreffend = true;
                    begruendung = 'Aus den Projektmerkmalen abgeleitet.';
                } else if (wert === false) {
                    zutreffend = false;
                    begruendung = 'Nach den Projektmerkmalen ausgeschlossen.';
                } else {
                    begruendung = 'Noch nicht geklärt.';
                }
            } else {
                begruendung = 'Noch nicht geklärt.';
            }
        } else {
            begruendung = ausdruecklich ? 'Ausdrückliche Angabe: trifft zu.' : 'Ausdrückliche Angabe: ausgeschlossen.';
        }

        if (a.nummer === 10 && zutreffend === true) {
            begruendung += ' Seit 01.04.2023 ohne 10-t-Grenze — nahezu jeder Fertigteilbau fällt darunter.';
        }

        return { nummer: a.nummer, bezeichnung: a.bezeichnung, zutreffend, begruendung };
    });
};

/**
 * Schwellenwertpruefung nach §§ 2 und 3 BaustellV.
 *
 * Die drei Ergebnisse haengen unterschiedlich zusammen:
 *   Vorankuendigung  = (> 30 Arbeitstage UND > 20 Beschaeftigte) ODER > 500 Personentage
 *   SiGe-Plan        = mehrere Arbeitgeber UND (Vorankuendigung ODER Anhang II)
 *   Koordinator      = mehrere Arbeitgeber
 *
 * "Noch nicht geklaert" ist NICHT "nein". Jede Pflicht traegt deshalb neben
 * dem Boolean einen dreiwertigen Stand (`PflichtStand`); `keine` steht dort
 * nur, wenn die Schwelle SICHER nicht faellt. Fehlt eine Eingangsgroesse, die
 * das Ergebnis noch kippen koennte, ist der Stand `ungeklaert`,
 * `unvollstaendig` gesetzt und das Ergebnis ausdruecklich vorlaeufig.
 */
export const pruefeSchwellenwerte = (eingang: SchwellenwertEingang): SchwellenwertErgebnis => {
    const arbeitstage = zahl(eingang.arbeitstage);
    const maxBeschaeftigte = zahl(eingang.maxBeschaeftigte);
    const personentage = zahl(eingang.personentage);
    const mehrereArbeitgeber = eingang.mehrereArbeitgeber;
    const merkmale = eingang.merkmale || {};
    const anhang2 = pruefeAnhang2(merkmale, eingang.anhang2Antworten);

    const regelA = typeof arbeitstage === 'number' && typeof maxBeschaeftigte === 'number'
        && arbeitstage > 30 && maxBeschaeftigte > 20;
    const regelB = typeof personentage === 'number' && personentage > 500;
    const vorankuendigungPflicht = regelA || regelB;

    // Regel A ist eine UND-Verknuepfung: EINE bekannte Zahl unter ihrer
    // Schwelle schliesst sie sicher aus, auch wenn die andere noch fehlt.
    const regelASicherNein = (typeof arbeitstage === 'number' && arbeitstage <= 30)
        || (typeof maxBeschaeftigte === 'number' && maxBeschaeftigte <= 20);
    // Personentage koennen Arbeitstage × Hoechstzahl der Beschaeftigten nicht
    // uebersteigen. Liegt dieses Produkt bei 500 oder darunter, ist Regel B
    // auch ohne die Zahl selbst ausgeschlossen. Umgekehrt NICHT: 40 Tage mit
    // bis zu 15 Beschaeftigten lassen 600 Personentage zu, die Schwelle kann
    // fallen. Bis hierher galt das Ergebnis schon als vollstaendig, sobald
    // Bauzeit und Beschaeftigtenzahl da waren — eine Entwarnung ohne Grundlage.
    const personentageHoechstens = typeof arbeitstage === 'number' && typeof maxBeschaeftigte === 'number'
        ? arbeitstage * maxBeschaeftigte
        : undefined;
    const regelBSicherNein = (typeof personentage === 'number' && personentage <= 500)
        || (typeof personentageHoechstens === 'number' && personentageHoechstens <= 500);

    const vorankuendigungStand: PflichtStand = vorankuendigungPflicht
        ? 'pflicht'
        : regelASicherNein && regelBSicherNein ? 'keine' : 'ungeklaert';

    let vorankuendigungBegruendung: string;
    if (regelA && regelB) {
        vorankuendigungBegruendung = `Beide Schwellen überschritten: ${arbeitstage} Arbeitstage bei ${maxBeschaeftigte} Beschäftigten und ${personentage} Personentage (§ 2 Abs. 2 BaustellV).`;
    } else if (regelA) {
        vorankuendigungBegruendung = `${arbeitstage} Arbeitstage (über 30) bei ${maxBeschaeftigte} gleichzeitig Beschäftigten (über 20) — § 2 Abs. 2 BaustellV.`;
    } else if (regelB) {
        vorankuendigungBegruendung = `${personentage} Personentage (über 500) — § 2 Abs. 2 BaustellV.`;
    } else if (vorankuendigungStand === 'ungeklaert') {
        // Nur nennen, was das Ergebnis noch kippen kann — eine fehlende Zahl,
        // die an einer sicher ausgeschlossenen Regel haengt, ist kein Mangel.
        const fehlt: string[] = [];
        if (!regelASicherNein) {
            if (typeof arbeitstage !== 'number') fehlt.push('Bauzeit');
            if (typeof maxBeschaeftigte !== 'number') fehlt.push('Beschäftigtenzahl');
        }
        if (!regelBSicherNein && typeof personentage !== 'number') fehlt.push('Personentage');
        const spanne = !regelBSicherNein && typeof personentageHoechstens === 'number'
            ? ` Bei ${arbeitstage} Arbeitstagen und bis zu ${maxBeschaeftigte} Beschäftigten sind bis zu ${personentageHoechstens} Personentage möglich (Schwelle 500).`
            : '';
        vorankuendigungBegruendung = `Noch nicht angegeben: ${fehlt.join(', ')} — Ergebnis vorläufig.${spanne}`;
    } else {
        const herleitung = typeof personentage !== 'number' && typeof personentageHoechstens === 'number'
            ? ` Personentage höchstens ${personentageHoechstens} (${arbeitstage} Arbeitstage × ${maxBeschaeftigte} Beschäftigte).`
            : '';
        vorankuendigungBegruendung = `Keine der beiden Schwellen des § 2 Abs. 2 BaustellV ist überschritten.${herleitung}`;
    }

    const anhang2Trifft = anhang2.some(a => a.zutreffend === true);
    const anhang2Offen = anhang2.some(a => a.zutreffend === undefined);
    const anhang2Nummern = anhang2.filter(a => a.zutreffend === true).map(a => a.nummer);

    const sigeplanPflicht = mehrereArbeitgeber === true && (vorankuendigungPflicht || anhang2Trifft);
    let sigeplanStand: PflichtStand;
    if (mehrereArbeitgeber === false) sigeplanStand = 'keine';
    else if (mehrereArbeitgeber === undefined) sigeplanStand = 'ungeklaert';
    else if (sigeplanPflicht) sigeplanStand = 'pflicht';
    else if (vorankuendigungStand === 'ungeklaert' || anhang2Offen) sigeplanStand = 'ungeklaert';
    else sigeplanStand = 'keine';

    let sigeplanBegruendung: string;
    if (mehrereArbeitgeber === false) {
        sigeplanBegruendung = 'Nur ein Arbeitgeber — kein SiGe-Plan. Der Verantwortliche muss den Arbeitgeber aber nach § 2 Abs. 4 BaustellV über die Umstände auf dem Gelände unterrichten.';
    } else if (mehrereArbeitgeber === undefined) {
        sigeplanBegruendung = 'Noch nicht geklärt, ob Beschäftigte mehrerer Arbeitgeber tätig werden.';
    } else if (sigeplanPflicht) {
        const gruende: string[] = [];
        if (vorankuendigungPflicht) gruende.push('Vorankündigung ist zu übermitteln');
        if (anhang2Trifft) gruende.push(`Arbeiten nach Anhang II Nr. ${anhang2Nummern.join(', ')}`);
        sigeplanBegruendung = `Mehrere Arbeitgeber und ${gruende.join(' sowie ')} — § 2 Abs. 3 BaustellV.`;
    } else if (sigeplanStand === 'ungeklaert') {
        // Beide Ausloeser des § 2 Abs. 3 koennen noch offen sein — nicht nur
        // Anhang II. Eine ungeklaerte Vorankuendigung las sich bisher hier als
        // „greift nicht".
        const offen: string[] = [];
        if (vorankuendigungStand === 'ungeklaert') offen.push('die Vorankündigungspflicht');
        if (anhang2Offen) offen.push('Anhang II');
        sigeplanBegruendung = `Mehrere Arbeitgeber, aber ${offen.join(' und ')} ${offen.length > 1 ? 'sind' : 'ist'} noch nicht abschließend geklärt — Ergebnis vorläufig.`;
    } else {
        sigeplanBegruendung = 'Mehrere Arbeitgeber, aber weder Vorankündigungspflicht noch Arbeiten nach Anhang II — § 2 Abs. 3 BaustellV greift nicht.';
    }

    const koordinatorPflicht = mehrereArbeitgeber === true;
    const koordinatorStand: PflichtStand = mehrereArbeitgeber === true
        ? 'pflicht'
        : mehrereArbeitgeber === false ? 'keine' : 'ungeklaert';
    const koordinatorBegruendung = mehrereArbeitgeber === true
        ? 'Beschäftigte mehrerer Arbeitgeber werden tätig — Koordinator ist zu bestellen (§ 3 Abs. 1 BaustellV). Die Beauftragung entbindet den Bauherrn nicht von seiner Verantwortung (§ 3 Abs. 1a).'
        : mehrereArbeitgeber === false
            ? 'Nur ein Arbeitgeber — keine Bestellpflicht nach § 3 Abs. 1 BaustellV.'
            : 'Noch nicht geklärt.';

    return {
        arbeitstage,
        maxBeschaeftigte,
        personentage,
        mehrereArbeitgeber,
        vorankuendigungPflicht,
        vorankuendigungBegruendung,
        sigeplanPflicht,
        sigeplanBegruendung,
        koordinatorPflicht,
        koordinatorBegruendung,
        vorankuendigungStand,
        sigeplanStand,
        koordinatorStand,
        anhang2,
        unvollstaendig: vorankuendigungStand === 'ungeklaert' || mehrereArbeitgeber === undefined || anhang2Offen,
        berechnet_am: eingang.jetztIso,
    };
};

const PFLICHT_STAENDE: ReadonlyArray<PflichtStand> = ['pflicht', 'keine', 'ungeklaert'];

/**
 * Der dreiwertige Stand einer Pflicht — die EINZIGE Lesestelle fuer Maske,
 * PDF und Folgemodule.
 *
 * `pruefung` ist ein gespeicherter Schnappschuss; Altbestand traegt die
 * `…Stand`-Felder nicht. Fuer ihn wird aus Boolean und `unvollstaendig`
 * zurueckgerechnet: `true` → pflicht, `false` bei unvollstaendiger Pruefung →
 * ungeklaert, sonst keine. Das ist bewusst grob — `unvollstaendig` gilt fuer
 * das ganze Ergebnis, nicht je Pflicht — aber es irrt nur in Richtung
 * „ungeklaert", nie in Richtung Entwarnung. Fehlt das Ergebnis ganz, ist
 * ebenfalls nichts geklaert.
 */
export const pflichtStandAus = (
    ergebnis: SchwellenwertErgebnis | null | undefined,
    welche: 'vorankuendigung' | 'sigeplan' | 'koordinator',
): PflichtStand => {
    if (!ergebnis) return 'ungeklaert';
    const stand = ergebnis[`${welche}Stand` as const];
    if (stand && PFLICHT_STAENDE.includes(stand)) return stand;
    if (ergebnis[`${welche}Pflicht` as const] === true) return 'pflicht';
    // Altbestand MIT Eingangsgroessen: nach heutiger Regel neu ableiten. Die
    // alte Pruefung nannte etwa 40 Arbeitstage bei 15 Beschaeftigten ohne
    // Personentage „vollstaendig" — bis zu 600 Personentage sind dabei
    // moeglich, die Entwarnung hatte keine Grundlage.
    const hatEingaenge = [ergebnis.arbeitstage, ergebnis.maxBeschaeftigte, ergebnis.personentage]
        .some(z => typeof z === 'number') || typeof ergebnis.mehrereArbeitgeber === 'boolean';
    if (hatEingaenge) {
        const anhang2Antworten: Record<number, boolean | undefined> = {};
        (ergebnis.anhang2 || []).forEach(a => { anhang2Antworten[a.nummer] = a.zutreffend; });
        const neu = pruefeSchwellenwerte({
            arbeitstage: ergebnis.arbeitstage,
            maxBeschaeftigte: ergebnis.maxBeschaeftigte,
            personentage: ergebnis.personentage,
            mehrereArbeitgeber: ergebnis.mehrereArbeitgeber,
            anhang2Antworten,
            jetztIso: ergebnis.berechnet_am,
        });
        const abgeleitet = neu[`${welche}Stand` as const];
        if (abgeleitet) return abgeleitet;
    }
    // SiGe-Plan (§ 2 Abs. 3) und Koordinator (§ 3 Abs. 1) setzen mehrere
    // Arbeitgeber voraus. Ist das ausdrücklich verneint, entfallen beide, auch
    // wenn der alte Schnappschuss wegen fehlender Personentage als
    // unvollständig gilt — sonst stünde dort „noch nicht geklärt" ohne Grund.
    if (welche !== 'vorankuendigung' && ergebnis.mehrereArbeitgeber === false) return 'keine';
    return ergebnis.unvollstaendig === true ? 'ungeklaert' : 'keine';
};

// ═══════════════════════════════════════════════════════════════════════════
// PFLICHTENSTAND — gemeinsame Lese-Schicht fuer Hub, Ampel und Folgeunterlagen
// ═══════════════════════════════════════════════════════════════════════════

/** Anzeigereihenfolge. `pflichtenStand` liefert immer genau diese fuenf. */
export const PFLICHT_ARTEN: ReadonlyArray<PflichtArt> = [
    'vorankuendigung', 'sigeplan', 'koordinator', 'ufsa', 'asplan',
];

export const PFLICHT_ART_LABEL: Record<PflichtArt, string> = {
    vorankuendigung: 'Vorankündigung',
    sigeplan: 'SiGe-Plan',
    koordinator: 'Koordinator',
    ufsa: 'Unterlage für spätere Arbeiten',
    asplan: 'A+S-Plan (Verdacht)',
};

/**
 * Fundstellen je Pflicht — ausschliesslich Wortlaut, der im Repo schon steht
 * (Begruendungen in `pruefeSchwellenwerte`, `recht` im Fragenkatalog, Kopf
 * des A+S-Moduls). Was hier nicht belegt ist, bleibt leer statt geraten.
 */
const PFLICHT_RECHT: Record<PflichtArt, string> = {
    vorankuendigung: '§ 2 Abs. 2 BaustellV',
    sigeplan: '§ 2 Abs. 3 BaustellV',
    koordinator: '§ 3 Abs. 1 BaustellV',
    ufsa: '§ 3 Abs. 2 Nr. 3 BaustellV',
    asplan: 'DGUV Regel 101-004 / TRGS 524',
};

/** Die drei Pflichten, die der Schnappschuss `pruefung` traegt. */
const SCHWELLEN_PFLICHTEN = ['vorankuendigung', 'sigeplan', 'koordinator'] as const;

/**
 * Alle fuenf Pflichten `ungeklaert` mit derselben Begruendung — fuer „noch
 * keine Planungsphase" und fuer den Lesefehler im Service. Nie `keine`: was
 * nicht gelesen werden konnte, ist nicht entwarnt.
 */
export const pflichtenUngeklaert = (begruendung: string): PflichtEintrag[] =>
    PFLICHT_ARTEN.map(art => ({
        art,
        stand: 'ungeklaert' as PflichtStand,
        begruendung,
        rechtsgrundlage: PFLICHT_RECHT[art],
    }));

/**
 * Die Schwellenwertpruefung aus den Merkmalen — derselbe Aufruf, mit dem
 * `PlanungModul.schreibeGrundlagen` den Schnappschuss `pruefung` schreibt und
 * `PlanungGrundlagen` die Vorschau rechnet. Eine zweite Rechenvorschrift
 * daneben waere die sichere Quelle fuer Abweichungen, die keine sind.
 */
export const schwellenwerteAusMerkmalen = (
    merkmale: ProjektMerkmale | null | undefined,
    jetztIso: string,
): SchwellenwertErgebnis => {
    const m = merkmale || {};
    return pruefeSchwellenwerte({
        arbeitstage: m.arbeitstage,
        maxBeschaeftigte: m.maxBeschaeftigte,
        personentage: m.personentage,
        mehrereArbeitgeber: m.mehrereArbeitgeber,
        merkmale: m,
        jetztIso,
    });
};

/**
 * Unterlage fuer spaetere Arbeiten: eine Koordinatorpflicht (§ 3 Abs. 2
 * Nr. 3 BaustellV). Ihr Stand IST der Stand der Koordinatorpflicht — ohne
 * Koordinator schuldet die BaustellV die Unterlage nicht, mit Koordinator
 * immer.
 */
const ufsaAusKoordinator = (ergebnis: SchwellenwertErgebnis): Pick<PflichtEintrag, 'stand' | 'begruendung'> => {
    const stand = pflichtStandAus(ergebnis, 'koordinator');
    if (stand === 'pflicht') {
        return {
            stand,
            begruendung: 'Koordinatorpflicht greift (Beschäftigte mehrerer Arbeitgeber werden tätig) — der Koordinator stellt die Unterlage mit den sicherheits- und gesundheitsrelevanten Angaben für mögliche spätere Arbeiten an der baulichen Anlage zusammen (§ 3 Abs. 2 Nr. 3 BaustellV).',
        };
    }
    if (stand === 'keine') {
        return {
            stand,
            begruendung: 'Nur ein Arbeitgeber — keine Koordinatorpflicht (§ 3 Abs. 1 BaustellV) und damit keine Unterlage für spätere Arbeiten nach § 3 Abs. 2 Nr. 3 BaustellV.',
        };
    }
    return {
        stand,
        begruendung: 'Hängt an der Koordinatorpflicht: noch nicht geklärt, ob Beschäftigte mehrerer Arbeitgeber tätig werden.',
    };
};

/** Wortlaut der Vermutungsregel aus dem Fragenkatalog (PL-E-01). */
const ASBEST_VERMUTUNG = 'bei Errichtung vor dem 31.10.1993 ist von asbesthaltigen Baustoffen auszugehen';
const ANHANG2_STOFFE = 'Umgang mit Stoffen nach Anhang II Nr. 2 BaustellV';
const ABBRUCH = 'Abbruch- oder Rückbauarbeiten';
/** Wortlaut aus dem Fragenkatalog (PL-D-05). */
const ALTLASTEN = 'Altlasten, Bodenkontaminationen oder Deponieverdacht im Baufeld';

/** Die zwei Wege zum A+S-Verdacht (Entscheid E4). */
export type AsplanWeg = 'gebaeudeschadstoffe' | 'altlasten';

/**
 * Die A+S-Verdachtsregel als Konstante (Entscheid E4): zwei Wege, getrennt
 * begruendet, jeder allein genuegt fuer `pflicht`; `keine` verlangt beide
 * ausdruecklich verneint. Fundstellen nur im Repo-Wortlaut (`recht` der
 * Katalogfragen PL-E-01 und PL-D-05).
 */
export const ASPLAN_VERDACHT_WEGE: ReadonlyArray<{
    weg: AsplanWeg;
    titel: string;
    merkmale: readonly (keyof ProjektMerkmale)[];
    rechtsgrundlage: string;
}> = [
    {
        weg: 'gebaeudeschadstoffe',
        titel: 'Gebäudeschadstoffe',
        merkmale: ['bestand', 'bestandBaujahr', 'gefahrstoffeAnhang2', 'abbruch'],
        rechtsgrundlage: 'GefStoffV § 5a',
    },
    {
        weg: 'altlasten',
        titel: 'Boden/Altlasten',
        merkmale: ['altlastenverdacht'],
        rechtsgrundlage: 'TRGS 524 / DGUV Regel 101-004',
    },
];

const WEG_TITEL = (weg: AsplanWeg): string => ASPLAN_VERDACHT_WEGE.find(w => w.weg === weg)?.titel || weg;

/** Teilergebnis eines Weges — die Begruendung nennt nur die Angaben dieses
 *  Weges; den Schluss zieht `asplanVerdacht` fuer beide zusammen. */
interface WegStand {
    stand: PflichtStand;
    begruendung: string;
    /** Es fehlt noch eine Angabe, die das Ergebnis kippen kann. */
    offen: boolean;
    /** Die Angaben widersprechen sich (Abbruch ohne Bestand). */
    widerspruch: boolean;
}

/**
 * Weg 1 — Gebaeudeschadstoffe: Gibt es Anhaltspunkte, dass in belasteter
 * Bausubstanz gearbeitet wird?
 *
 *   pflicht    — Bauen im Bestand UND (Baujahr vor 1993 ODER Stoffe nach
 *                Anhang II Nr. 2 ODER Abbruch)
 *   keine      — Bestand UND Anhang-II-Stoffe ausdruecklich verneint, kein
 *                Abbruch angegeben
 *   ungeklaert — alles andere, allen voran Bestand ja mit fehlendem Baujahr
 *
 * Das Baujahr traegt die Vermutungsregel des Fragenkatalogs (PL-E-01,
 * GefStoffV § 5a): vor dem 31.10.1993 ist von asbesthaltigen Baustoffen
 * auszugehen. Das Jahr 1993 selbst entscheidet nicht — ob vor oder nach dem
 * Stichtag errichtet, steht nicht im Jahr. Ein Bestand ohne Auffaelligkeit
 * bleibt `ungeklaert`, nicht `keine`: ohne Erkundung gibt es keine
 * Entwarnung.
 */
const gebaeudeschadstoffVerdacht = (merkmale: ProjektMerkmale): WegStand => {
    const bestand = merkmale.bestand;
    const gefahrstoffe = merkmale.gefahrstoffeAnhang2;
    const abbruch = merkmale.abbruch;
    const roh = merkmale.bestandBaujahr;
    const baujahr = typeof roh === 'number' && isFinite(roh) && roh > 0 ? Math.trunc(roh) : undefined;
    const vorStichtag = typeof baujahr === 'number' && baujahr < 1993;

    if (bestand === true && (vorStichtag || gefahrstoffe === true || abbruch === true)) {
        const gruende: string[] = [];
        if (vorStichtag) gruende.push(`Baujahr ${baujahr} — ${ASBEST_VERMUTUNG} (GefStoffV § 5a)`);
        if (gefahrstoffe === true) gruende.push(ANHANG2_STOFFE);
        if (abbruch === true) gruende.push(ABBRUCH);
        return { stand: 'pflicht', begruendung: `Bauen im Bestand: ${gruende.join('; ')}.`, offen: false, widerspruch: false };
    }

    if (bestand === false && gefahrstoffe === false && abbruch !== true) {
        return {
            stand: 'keine',
            begruendung: `Kein Bauen im Bestand und kein ${ANHANG2_STOFFE}.`,
            offen: false,
            widerspruch: false,
        };
    }

    // Ungeklaert: nennen, was bekannt ist und was noch fehlt — sonst steht
    // dort „noch nicht geklaert" ohne Hinweis, welche Angabe das aendert.
    const bekannt: string[] = [];
    const offen: string[] = [];
    if (bestand === true) bekannt.push('Bauen im Bestand');
    else if (bestand === false) bekannt.push('kein Bauen im Bestand');
    else offen.push('ob im Bestand gebaut wird');
    if (bestand !== false) {
        if (typeof baujahr !== 'number') offen.push(`das Baujahr (${ASBEST_VERMUTUNG})`);
        else if (baujahr === 1993) offen.push('ob das Gebäude vor oder nach dem 31.10.1993 errichtet wurde — das Baujahr 1993 allein entscheidet das nicht');
        else bekannt.push(`Baujahr ${baujahr} (nach dem 31.10.1993, aus dem Baujahr keine Asbestvermutung)`);
    }
    if (gefahrstoffe === true) bekannt.push(ANHANG2_STOFFE);
    else if (gefahrstoffe === false) bekannt.push(`kein ${ANHANG2_STOFFE}`);
    else offen.push('ob mit Stoffen nach Anhang II Nr. 2 BaustellV umgegangen wird');
    const widerspruch = bestand === false && abbruch === true;
    if (abbruch === true) bekannt.push(widerspruch ? `${ABBRUCH} — widerspricht „kein Bauen im Bestand"` : ABBRUCH);
    else if (abbruch === undefined) offen.push(`ob ${ABBRUCH} anstehen`);

    const angaben = bekannt.length ? `Angaben: ${bekannt.join(', ')}.` : '';
    const fehlt = offen.length ? ` Noch nicht geklärt: ${offen.join('; ')}.` : '';
    return { stand: 'ungeklaert', begruendung: `${angaben}${fehlt}`.trim(), offen: offen.length > 0, widerspruch };
};

/**
 * Weg 2 — Boden/Altlasten: das Merkmal `altlastenverdacht` (PL-D-05),
 * dreiwertig. Ein Neubau auf belastetem Grund loest den Verdacht allein aus;
 * nur ein ausdrueckliches Nein entwarnt diesen Weg.
 */
const altlastenVerdacht = (merkmale: ProjektMerkmale): WegStand => {
    const v = merkmale.altlastenverdacht;
    if (v === true) {
        return { stand: 'pflicht', begruendung: `${ALTLASTEN} angegeben (PL-D-05).`, offen: false, widerspruch: false };
    }
    if (v === false) {
        return { stand: 'keine', begruendung: `Kein ${ALTLASTEN} angegeben.`, offen: false, widerspruch: false };
    }
    return { stand: 'ungeklaert', begruendung: `Noch nicht geklärt, ob ${ALTLASTEN} bestehen.`, offen: true, widerspruch: false };
};

/**
 * A+S-Verdacht aus beiden Wegen (Entscheid E4): Gibt es Anhaltspunkte, dass
 * in kontaminierten Bereichen gearbeitet wird und der Arbeits- und
 * Sicherheitsplan nach DGUV Regel 101-004 gebraucht wird?
 *
 *   pflicht    — EIN Weg ergibt `pflicht`
 *   keine      — BEIDE Wege ergeben `keine` (Bestand, Anhang-II-Stoffe und
 *                Altlastenverdacht ausdruecklich verneint, kein Abbruch)
 *   ungeklaert — alles andere
 *
 * Die Begruendung nennt beide Wege getrennt und zieht danach den Schluss.
 */
const asplanVerdacht = (merkmale: ProjektMerkmale): Pick<PflichtEintrag, 'stand' | 'begruendung'> => {
    const gebaeude = gebaeudeschadstoffVerdacht(merkmale);
    const boden = altlastenVerdacht(merkmale);
    const wege = `${WEG_TITEL('gebaeudeschadstoffe')}: ${gebaeude.begruendung} ${WEG_TITEL('altlasten')}: ${boden.begruendung}`;

    if (gebaeude.stand === 'pflicht' || boden.stand === 'pflicht') {
        return {
            stand: 'pflicht',
            begruendung: `${wege} Verdacht auf Arbeiten in kontaminierten Bereichen — der Arbeits- und Sicherheitsplan nach DGUV Regel 101-004 wird gebraucht, sobald in kontaminierten Bereichen gearbeitet wird.`,
        };
    }
    if (gebaeude.stand === 'keine' && boden.stand === 'keine') {
        return { stand: 'keine', begruendung: `${wege} Kein Anhaltspunkt für Arbeiten in kontaminierten Bereichen.` };
    }

    const schluss: string[] = [];
    if (gebaeude.widerspruch) schluss.push('Die Angaben zum Bestand widersprechen sich — bitte klären.');
    if (gebaeude.offen || boden.offen) schluss.push('Ergebnis vorläufig — keine Entwarnung.');
    else if (!gebaeude.widerspruch) schluss.push('Ob in kontaminierten Bereichen gearbeitet wird, ist damit nicht geklärt — ohne Erkundung keine Entwarnung.');
    return { stand: 'ungeklaert', begruendung: `${wege} ${schluss.join(' ')}`.trim() };
};

/**
 * Stand aller fuenf Pflichten, LIVE aus den Merkmalen gerechnet.
 *
 * Nicht aus dem gespeicherten Schnappschuss `pruefung`: der entsteht nur beim
 * Speichern der Grundlagen und kann hinter den Merkmalen zurueckbleiben
 * (`erzeugeKatalog` schreibt Merkmale ohne Pruefung). Ob er abweicht, sagt
 * `pruefungWeichtAb`. Ohne Planungsphase ist nichts geklaert — alle fuenf
 * `ungeklaert`, nie `keine`.
 *
 * `jetztIso` ist ein Zeitstempel fuer `berechnet_am` der Zwischenrechnung;
 * er beeinflusst keinen Stand.
 */
export const pflichtenStand = (
    phase: Pick<Planungsphase, 'merkmale'> | null | undefined,
    jetztIso: string,
): PflichtEintrag[] => {
    if (!phase) return pflichtenUngeklaert('Planungsphase noch nicht angelegt.');
    const merkmale = phase.merkmale || {};
    const ergebnis = schwellenwerteAusMerkmalen(merkmale, jetztIso);
    const ufsa = ufsaAusKoordinator(ergebnis);
    const asplan = asplanVerdacht(merkmale);
    return [
        {
            art: 'vorankuendigung',
            stand: pflichtStandAus(ergebnis, 'vorankuendigung'),
            begruendung: ergebnis.vorankuendigungBegruendung,
            rechtsgrundlage: PFLICHT_RECHT.vorankuendigung,
        },
        {
            art: 'sigeplan',
            stand: pflichtStandAus(ergebnis, 'sigeplan'),
            begruendung: ergebnis.sigeplanBegruendung,
            rechtsgrundlage: PFLICHT_RECHT.sigeplan,
        },
        {
            art: 'koordinator',
            stand: pflichtStandAus(ergebnis, 'koordinator'),
            begruendung: ergebnis.koordinatorBegruendung,
            rechtsgrundlage: PFLICHT_RECHT.koordinator,
        },
        { art: 'ufsa', ...ufsa, rechtsgrundlage: PFLICHT_RECHT.ufsa },
        { art: 'asplan', ...asplan, rechtsgrundlage: PFLICHT_RECHT.asplan },
    ];
};

/**
 * Weicht der gespeicherte Schnappschuss `pruefung` bei einer der drei
 * Schwellen-Pflichten vom Live-Ergebnis aus den Merkmalen ab?
 *
 * Beide Seiten werden ueber `pflichtStandAus` gelesen, damit ein Altbestand
 * ohne Stand-Felder nicht schon deshalb als Abweichung gilt. Ein fehlender
 * Schnappschuss (nie gespeichert) liest sich als „alles ungeklaert" und
 * weicht ab, sobald die Merkmale etwas Bestimmtes ergeben — genau dann soll
 * die Maske zum Speichern auffordern. Ohne Planungsphase gibt es nichts,
 * das abweichen koennte.
 */
export const pruefungWeichtAb = (
    phase: Pick<Planungsphase, 'merkmale' | 'pruefung'> | null | undefined,
    jetztIso: string,
): boolean => {
    if (!phase) return false;
    const live = schwellenwerteAusMerkmalen(phase.merkmale, jetztIso);
    return SCHWELLEN_PFLICHTEN.some(w => pflichtStandAus(live, w) !== pflichtStandAus(phase.pruefung, w));
};

// ═══════════════════════════════════════════════════════════════════════════
// FRISTEN UND STATUS
// ═══════════════════════════════════════════════════════════════════════════

/** Vorgabefrist in Tagen fuer eigene Fragen und fuer Codes, die der Katalog
 *  nicht kennt — dieselbe Spanne wie fuer B- und C-Fragen des Katalogs. */
export const FRIST_TAGE_STANDARD = 21;

/**
 * Rueckantwortfrist in Tagen fuer einen Fragencode.
 *
 * Liest `fristTage` aus dem Katalog (A-Fragen 14, B/C 21 Tage, siehe
 * `fragenkatalog.ts`). Eigene Fragen (`PL-X-…`) und unbekannte Codes
 * bekommen den Standard. Beim Stellen wird je Fristgruppe gestempelt, damit
 * nicht EINE Frist fuer alle ausgewaehlten Fragen gilt.
 */
export const fristTageFuerCode = (code: string | null | undefined): number => {
    const gesucht = (code || '').trim();
    const treffer = gesucht ? FRAGEN_KATALOG.find(f => f.code === gesucht) : undefined;
    const tage = treffer ? treffer.fristTage : undefined;
    return typeof tage === 'number' && isFinite(tage) && tage > 0 ? Math.round(tage) : FRIST_TAGE_STANDARD;
};

/**
 * Tagesdatum in Ortszeit (YYYY-MM-DD).
 *
 * `toISOString()` liefert den UTC-Tag: zwischen Mitternacht und ein bzw. zwei
 * Uhr ist das in Deutschland noch der Vortag — und eine Frist, die heute
 * ablaeuft, gaelte dann als noch offen.
 */
export const heuteLokalIso = (jetzt: Date = new Date()): string => {
    const monat = String(jetzt.getMonth() + 1).padStart(2, '0');
    const tag = String(jetzt.getDate()).padStart(2, '0');
    return `${jetzt.getFullYear()}-${monat}-${tag}`;
};

/** ISO-Tag → TT.MM.JJJJ durch reines Zerlegen. Kein Date-Parsing: das
 *  verschiebt ein Tagesdatum je nach Zeitzone um einen Tag. Leer bei
 *  unbrauchbarer Eingabe. */
const tagDeutsch = (iso: string | null | undefined): string => {
    const t = String(iso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
    const [j, m, d] = t.split('-');
    return `${d}.${m}.${j}`;
};

/** ISO-Datum (YYYY-MM-DD) plus Tage. Rechnet in UTC, damit die Sommerzeit
 *  nicht gelegentlich einen Tag verschluckt. */
export const fristDatum = (abIso: string, tage: number): string => {
    const basis = (abIso || '').slice(0, 10);
    const t = Date.parse(`${basis}T00:00:00Z`);
    if (!isFinite(t)) return '';
    const d = new Date(t + Math.round(tage) * 86400000);
    return d.toISOString().slice(0, 10);
};

/** ISO-Datumsstrings sind lexikografisch vergleichbar — kein Date-Parsing. */
export const istUeberfaellig = (frage: Planungsfrage, heuteIso: string): boolean => {
    if (!frage || !frage.frist_am) return false;
    if (frage.status === 'beantwortet' || frage.status === 'gegenstandslos') return false;
    return frage.frist_am.slice(0, 10) < (heuteIso || '').slice(0, 10);
};

/**
 * Die verstrichene Frist einer unerledigten Frage als ISO-Tag, sonst `null`.
 *
 * Liest `frist_am`, nicht den Status: der Fristablauf ist ein Fakt, der
 * Status `nicht_beantwortet` die Feststellung des Koordinators dazu — und die
 * kann Tage hinterherhinken. Auf dem Blatt muss der Fakt stehen.
 */
export const fristAbgelaufenAm = (frage: Planungsfrage, heuteIso: string): string | null =>
    istUeberfaellig(frage, heuteIso) ? (frage.frist_am || '').slice(0, 10) : null;

/** „Frist abgelaufen am TT.MM.JJJJ" — oder '' wenn nichts abgelaufen ist.
 *  Ein Text fuer PDF und Restrisikoliste, damit beide dasselbe sagen. */
export const fristAbgelaufenText = (frage: Planungsfrage, heuteIso: string): string => {
    const tag = fristAbgelaufenAm(frage, heuteIso);
    return tag ? `Frist abgelaufen am ${tagDeutsch(tag) || tag}` : '';
};

export type Ampel = 'grau' | 'blau' | 'gelb' | 'gruen' | 'rot';

/**
 * Farbe einer Frage.
 *   grau  = noch nicht gestellt
 *   blau  = gestellt, Frist laeuft
 *   gelb  = teilbeantwortet
 *   gruen = beantwortet oder gegenstandslos
 *   rot   = Frist abgelaufen ohne Antwort
 */
export const ampelFuerFrage = (frage: Planungsfrage, heuteIso: string): Ampel => {
    if (!frage) return 'grau';
    if (frage.status === 'beantwortet' || frage.status === 'gegenstandslos') return 'gruen';
    if (frage.status === 'nicht_beantwortet') return 'rot';
    if (istUeberfaellig(frage, heuteIso)) return 'rot';
    if (frage.status === 'teilbeantwortet') return 'gelb';
    if (frage.status === 'gestellt') return 'blau';
    return 'grau';
};

/**
 * Fragen, deren Frist abgelaufen ist, wandern von `gestellt` auf
 * `nicht_beantwortet`. Gibt nur die Aenderungen zurueck, damit der Aufrufer
 * genau diese Zeilen schreibt und nicht die ganze Liste.
 */
export const faelligeStatuswechsel = (
    fragen: Planungsfrage[],
    heuteIso: string,
): { id: string; status: FrageStatus }[] =>
    (fragen || [])
        .filter(f => f.status === 'gestellt' && istUeberfaellig(f, heuteIso))
        .map(f => ({ id: f.id, status: 'nicht_beantwortet' as FrageStatus }));

// ═══════════════════════════════════════════════════════════════════════════
// KENNZAHLEN, FILTER, GRUPPIERUNG
// ═══════════════════════════════════════════════════════════════════════════

export const berechneKennzahlen = (fragen: Planungsfrage[], heuteIso: string): PlanungKennzahlen => {
    const liste = fragen || [];
    const zaehle = (s: FrageStatus) => liste.filter(f => f.status === s).length;
    const beantwortet = zaehle('beantwortet');
    const gegenstandslos = zaehle('gegenstandslos');
    // Der Nenner laesst gegenstandslose Fragen aussen vor: sie sind erledigt,
    // ohne beantwortet zu sein, und wuerden die Quote sonst dauerhaft deckeln.
    const nenner = liste.length - gegenstandslos;
    return {
        gesamt: liste.length,
        offen: zaehle('offen'),
        gestellt: zaehle('gestellt'),
        beantwortet,
        nichtBeantwortet: zaehle('nicht_beantwortet'),
        gegenstandslos,
        offenKritisch: liste.filter(f => f.kritikalitaet === 'A'
            && f.status !== 'beantwortet' && f.status !== 'gegenstandslos').length,
        ueberfaellig: liste.filter(f => istUeberfaellig(f, heuteIso)).length,
        quote: nenner > 0 ? Math.round((beantwortet / nenner) * 100) : 0,
    };
};

export interface FragenFilter {
    kapitel?: KatalogKapitel | 'alle';
    status?: FrageStatus | 'alle' | 'unerledigt';
    kritikalitaet?: FrageKritikalitaet | 'alle';
    adressat?: FrageAdressat | 'alle';
    suche?: string;
    nurUeberfaellig?: boolean;
}

export const filterFragen = (
    fragen: Planungsfrage[],
    filter: FragenFilter,
    heuteIso: string,
): Planungsfrage[] => {
    const f = filter || {};
    const suche = (f.suche || '').trim().toLowerCase();
    return (fragen || []).filter(q => {
        if (f.kapitel && f.kapitel !== 'alle' && q.kapitel !== f.kapitel) return false;
        if (f.kritikalitaet && f.kritikalitaet !== 'alle' && q.kritikalitaet !== f.kritikalitaet) return false;
        if (f.adressat && f.adressat !== 'alle' && !(q.adressat || []).includes(f.adressat)) return false;
        if (f.nurUeberfaellig && !istUeberfaellig(q, heuteIso)) return false;
        if (f.status && f.status !== 'alle') {
            if (f.status === 'unerledigt') {
                if (q.status === 'beantwortet' || q.status === 'gegenstandslos') return false;
            } else if (q.status !== f.status) return false;
        }
        if (suche) {
            const heu = `${q.code} ${q.frage} ${q.antwort} ${q.bewertung}`.toLowerCase();
            if (!heu.includes(suche)) return false;
        }
        return true;
    });
};

const KAPITEL_ORDNUNG = 'ABCDEFGHIJKLMN';

/** Nach Kapitel, dann nach Sortierung, dann nach Code — stabil und
 *  unabhaengig von der Ladereihenfolge aus der Datenbank. */
export const sortiereFragen = (fragen: Planungsfrage[]): Planungsfrage[] =>
    [...(fragen || [])].sort((a, b) => {
        const ka = KAPITEL_ORDNUNG.indexOf(a.kapitel);
        const kb = KAPITEL_ORDNUNG.indexOf(b.kapitel);
        if (ka !== kb) return ka - kb;
        if ((a.sortierung || 0) !== (b.sortierung || 0)) return (a.sortierung || 0) - (b.sortierung || 0);
        return (a.code || '').localeCompare(b.code || '', 'de', { numeric: true });
    });

export const gruppiereNachKapitel = (
    fragen: Planungsfrage[],
): { kapitel: KatalogKapitel; fragen: Planungsfrage[] }[] => {
    const map = new Map<KatalogKapitel, Planungsfrage[]>();
    sortiereFragen(fragen).forEach(f => {
        const bisher = map.get(f.kapitel);
        if (bisher) bisher.push(f); else map.set(f.kapitel, [f]);
    });
    return Array.from(map.entries()).map(([kapitel, liste]) => ({ kapitel, fragen: liste }));
};

/**
 * Fragen fuer EINEN Empfaenger. Der Sammelbrief an alle ist der Hauptgrund,
 * warum solche Kataloge unbeantwortet bleiben — der Tragwerksplaner
 * ueberliest 190 Fragen, von denen ihn zwoelf betreffen.
 */
export const fragenFuerAdressat = (
    fragen: Planungsfrage[],
    adressat: FrageAdressat,
    nurUnerledigt = true,
): Planungsfrage[] =>
    sortiereFragen((fragen || []).filter(f => {
        if (!(f.adressat || []).includes(adressat)) return false;
        if (!nurUnerledigt) return true;
        return f.status !== 'beantwortet' && f.status !== 'gegenstandslos';
    }));

/** Welche Adressaten kommen in der Liste ueberhaupt vor, mit Anzahl offener
 *  Fragen — Grundlage der Empfaengerauswahl beim Export. */
export const adressatenUebersicht = (
    fragen: Planungsfrage[],
): { adressat: FrageAdressat; offen: number; gesamt: number }[] => {
    const map = new Map<FrageAdressat, { offen: number; gesamt: number }>();
    (fragen || []).forEach(f => {
        (f.adressat || []).forEach(a => {
            const e = map.get(a) || { offen: 0, gesamt: 0 };
            e.gesamt += 1;
            if (f.status !== 'beantwortet' && f.status !== 'gegenstandslos') e.offen += 1;
            map.set(a, e);
        });
    });
    return Array.from(map.entries())
        .map(([adressat, v]) => ({ adressat, ...v }))
        .sort((a, b) => b.offen - a.offen || a.adressat.localeCompare(b.adressat));
};

// ═══════════════════════════════════════════════════════════════════════════
// FREIGABE-GATE UND RESTRISIKOLISTE
// ═══════════════════════════════════════════════════════════════════════════

const hinweisDecktCode = (hinweise: Planungshinweis[], code: string): number[] =>
    (hinweise || [])
        .filter(h => (h.art === 'bedenken' || h.art === 'gefahr')
            && !!h.versendet_am
            && (h.frage_codes || []).includes(code))
        .map(h => h.nummer);

/**
 * Die Planungsphase kann erst abgeschlossen werden, wenn keine A-kritische
 * Frage mehr offen ist — oder fuer jede offene A-Frage eine VERSENDETE
 * Bedenkenanzeige vorliegt.
 *
 * Das ist die Uebersetzung der Haftungslage in eine Zustandsmaschine: Der
 * Koordinator schuldet nicht, dass der Bauherr antwortet. Er schuldet, dass
 * er gefragt und bei ausbleibender Antwort foermlich Bedenken angemeldet
 * hat. Ein Entwurf im System genuegt dafuer nicht — deshalb `versendet_am`.
 */
export const pruefeFreigabe = (
    fragen: Planungsfrage[],
    hinweise: Planungshinweis[],
): FreigabePruefung => {
    const offeneKritische = (fragen || []).filter(f =>
        f.kritikalitaet === 'A' && f.status !== 'beantwortet' && f.status !== 'gegenstandslos');

    const blockierendeCodes: string[] = [];
    const gedeckteCodes: string[] = [];
    offeneKritische.forEach(f => {
        if (hinweisDecktCode(hinweise, f.code).length) gedeckteCodes.push(f.code);
        else blockierendeCodes.push(f.code);
    });

    const freigabeMoeglich = blockierendeCodes.length === 0;
    let hinweis: string;
    if (!(fragen || []).length) {
        hinweis = 'Es liegen noch keine Fragen vor. Bitte zuerst die Baubeschreibung erfassen und den Katalog erzeugen.';
    } else if (freigabeMoeglich && gedeckteCodes.length) {
        hinweis = `Freigabe möglich. ${gedeckteCodes.length} haftungsrelevante Frage(n) sind unbeantwortet geblieben, aber durch eine versendete Bedenkenanzeige gedeckt — sie gehen in die Restrisikoliste.`;
    } else if (freigabeMoeglich) {
        hinweis = 'Freigabe möglich. Alle haftungsrelevanten Fragen sind beantwortet oder gegenstandslos.';
    } else {
        hinweis = `${blockierendeCodes.length} haftungsrelevante Frage(n) sind weder beantwortet noch durch eine versendete Bedenkenanzeige gedeckt.`;
    }

    return { freigabeMoeglich, blockierendeCodes, gedeckteCodes, hinweis };
};

/**
 * Alles, was nicht abschliessend geklaert ist, wandert in die Uebergabe an
 * die Ausfuehrungsphase (§ 3 Abs. 2 → Abs. 3 BaustellV).
 *
 * `heuteIso` entscheidet, welche Fristen als abgelaufen gelten. Der Aufrufer
 * soll den Tag vorgeben (Ortszeit); der Rueckfall auf den heutigen Tag steht
 * nur, damit bestehende Aufrufer ohne Datum weiterlaufen.
 */
export const restrisikoliste = (
    fragen: Planungsfrage[],
    hinweise: Planungshinweis[],
    heuteIso: string = heuteLokalIso(),
): Restrisiko[] =>
    sortiereFragen((fragen || []).filter(f =>
        f.status !== 'beantwortet' && f.status !== 'gegenstandslos'))
        .map(f => ({
            code: f.code,
            kapitel: f.kapitel,
            frage: f.frage,
            status: f.status,
            kritikalitaet: f.kritikalitaet,
            adressat: f.adressat || [],
            hinweisNummern: hinweisDecktCode(hinweise, f.code),
            fristAbgelaufenAm: fristAbgelaufenAm(f, heuteIso),
            fristHinweis: fristAbgelaufenText(f, heuteIso),
        }));

// ═══════════════════════════════════════════════════════════════════════════
// BEDENKENANZEIGE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Pruefung der Wirksamkeitsvoraussetzungen.
 *
 * Eine formal schwache Bedenkenanzeige enthaftet nicht — das ist der Kern
 * des Ganzen. Die Rechtsprechung zu § 4 Abs. 3 VOB/B verlangt: richtiger
 * Adressat (der Bauherr selbst, nicht nur der Architekt), rechtzeitig,
 * inhaltlich vollstaendig mit KONKRETEN nachteiligen Folgen, klar als
 * Bedenkenanzeige bezeichnet, nachweisbar in Textform, mit Frist.
 *
 * Fuer den blossen `hinweis` gilt das nicht — dort wird nur der Betreff
 * verlangt.
 */
export const pruefeHinweis = (h: Partial<Planungshinweis>): string[] => {
    const fehlt: string[] = [];
    const leer = (s?: string | null) => !((s || '').trim());

    if (leer(h.betreff)) fehlt.push('Betreff fehlt.');
    if (leer(h.empfaenger_name)) fehlt.push('Empfänger fehlt.');

    if (h.art === 'hinweis') return fehlt;

    if (leer(h.sachverhalt)) fehlt.push('Sachverhalt fehlt — der pauschale Hinweis genügt nicht.');
    if (leer(h.gefaehrdung)) fehlt.push('Die konkrete Gefährdung ist nicht benannt.');
    if (leer(h.folgen)) fehlt.push('Die konkreten nachteiligen Folgen fehlen — ohne sie ist die Anzeige unwirksam.');
    if (leer(h.forderung)) fehlt.push('Es fehlt, welche Entscheidung oder Abhilfe verlangt wird.');
    if (leer(h.frist_am)) fehlt.push('Es fehlt eine angemessene Frist zur Rückäußerung.');
    if (leer(h.folge_bei_fristablauf)) fehlt.push('Es fehlt die Angabe, was bei Fristablauf geschieht.');
    if (!h.adressat_ist_bauherr) {
        fehlt.push('Adressat ist nicht der Bauherr bzw. der nach § 4 BaustellV Verantwortliche — eine Anzeige nur an den Architekten enthaftet regelmäßig nicht.');
    }
    if (h.art === 'gefahr' && leer(h.forderung)) {
        fehlt.push('Bei Gefahr im Verzug muss das Einstellungsverlangen ausdrücklich formuliert sein.');
    }
    return fehlt;
};

export const naechsteHinweisNummer = (hinweise: Planungshinweis[]): number =>
    (hinweise || []).reduce((a, h) => Math.max(a, h.nummer || 0), 0) + 1;

export const HINWEIS_ART_LABEL: Record<string, string> = {
    hinweis: 'Hinweis / Empfehlung',
    bedenken: 'Bedenkenanzeige',
    gefahr: 'Gefahrenanzeige mit Einstellungsverlangen',
};

/**
 * Baut aus offenen Fragen den Entwurf einer Bedenkenanzeige vor. Der Text
 * ist bewusst ein Geruest und keine fertige Anzeige — was fehlt, muss der
 * Koordinator selbst schreiben, sonst steht eine Floskel dort, wo die
 * konkrete Folge stehen muss.
 */
export const bedenkenEntwurfAusFragen = (fragen: Planungsfrage[]): {
    betreff: string;
    sachverhalt: string;
    rechtsgrundlage: string[];
    frage_codes: string[];
} => {
    const liste = sortiereFragen(fragen || []);
    const codes = liste.map(f => f.code);
    const rechte = Array.from(new Set(liste.flatMap(f => f.rechtsgrundlage || [])));
    const betreff = liste.length === 1
        ? `Bedenken zu ${liste[0].code}`
        : `Bedenken zu ${codes.length} offenen Punkten der Planungsphase`;
    const sachverhalt = liste.map(f => {
        const gestellt = f.gestellt_am ? ` (gestellt am ${f.gestellt_am.slice(0, 10)}` : '';
        const frist = f.frist_am ? `, Frist ${f.frist_am.slice(0, 10)})` : gestellt ? ')' : '';
        return `${f.code}: ${f.frage}${gestellt}${frist}`;
    }).join('\n\n');
    return { betreff, sachverhalt, rechtsgrundlage: rechte, frage_codes: codes };
};

// ═══════════════════════════════════════════════════════════════════════════
// SONSTIGES
// ═══════════════════════════════════════════════════════════════════════════

export const STATUS_LABEL: Record<FrageStatus, string> = {
    offen: 'Offen',
    gestellt: 'Gestellt',
    beantwortet: 'Beantwortet',
    teilbeantwortet: 'Teilweise beantwortet',
    nicht_beantwortet: 'Nicht beantwortet',
    gegenstandslos: 'Gegenstandslos',
};

/** Aktuelle Katalogfassung — der Service schreibt sie beim Anlegen fest. */
export const aktuelleKatalogVersion = (): number => KATALOG_VERSION;

// ═══════════════════════════════════════════════════════════════════════════
// ANGABEN FUER DIE FOLGEUNTERLAGEN — Gebaeudetyp, Dritter nach § 4 BaustellV
// ═══════════════════════════════════════════════════════════════════════════

/** Nur bekannte Werte; alles andere (leer, Altbestand, Tippfehler) heisst
 *  „noch nicht geklaert" — nie stillschweigend `sonstiges`. */
export const gebaeudetypAus = (wert: unknown): Gebaeudetyp | undefined =>
    typeof wert === 'string' && (GEBAEUDETYP_WERTE as readonly string[]).includes(wert)
        ? (wert as Gebaeudetyp)
        : undefined;

export const DRITTER_FELDER: readonly (keyof DritterNachPara4)[] = ['name', 'anschrift', 'rolle'];

/** Kein Feld mit Inhalt — dann wird das Merkmal gar nicht gespeichert. */
export const dritterLeer = (d: DritterNachPara4 | null | undefined): boolean =>
    !d || DRITTER_FELDER.every(f => !String(d[f] || '').trim());

/**
 * Ein Feld des Dritten setzen. Leert der Nutzer das letzte Feld, faellt das
 * Merkmal weg (`undefined`) — ein leeres Objekt saehe im Steckbrief wie ein
 * erfasster Dritter ohne Namen aus. Nicht getrimmt: waehrend des Tippens
 * stehen Leerzeichen am Ende, die beim naechsten Zeichen wieder gebraucht
 * werden; der Steckbrief trimmt beim Lesen.
 */
export const setzeDritterFeld = (
    alt: DritterNachPara4 | undefined,
    feld: keyof DritterNachPara4,
    wert: string,
): DritterNachPara4 | undefined => {
    const neu: DritterNachPara4 = { ...(alt || {}) };
    const t = String(wert ?? '');
    if (t.trim()) neu[feld] = t; else delete neu[feld];
    return dritterLeer(neu) ? undefined : neu;
};

/** Einzeiler „Name, Anschrift (Rolle)" fuer Anzeige und Blatt; leer ohne Inhalt. */
export const dritterText = (d: DritterNachPara4 | null | undefined): string => {
    if (dritterLeer(d)) return '';
    const name = String(d!.name || '').trim();
    const anschrift = String(d!.anschrift || '').trim();
    const rolle = String(d!.rolle || '').trim();
    const kopf = [name, anschrift].filter(Boolean).join(', ');
    return rolle ? (kopf ? `${kopf} (${rolle})` : rolle) : kopf;
};

/** Zaehlt, wie viele Katalogfragen die Merkmale ueberhaupt ausloesen.
 *  Vorschau in der Erfassungsmaske: "Ihre Angaben ergeben 74 Fragen."
 *  `spaeter`: ausgeloest, aber erst ab einer hoeheren Leistungsphase faellig
 *  — sie zaehlen NICHT in `ausgeloest`. */
export const zaehleAusgeloest = (
    merkmale: ProjektMerkmale,
    katalog: KatalogFrage[] = FRAGEN_KATALOG,
    lph: number | null = null,
): { ausgeloest: number; gesamt: number; kritisch: number; spaeter: number } => {
    const zutreffend = katalog.filter(f => frageTrifftZu(f, merkmale));
    const treffer = zutreffend.filter(f => frageFaellig(f, lph));
    return {
        ausgeloest: treffer.length,
        gesamt: katalog.length,
        kritisch: treffer.filter(f => f.kritikalitaet === 'A').length,
        spaeter: zutreffend.length - treffer.length,
    };
};
